export const SUPPORTED_ACCOUNT_LOCALE_CODES = [
  "en",
  "es-ES",
  "es-419",
  "ko",
  "ru",
  "pt-BR",
  "fr-FR",
  "fr-CA",
  "zh-Hans",
  "zh-Hant",
  "de",
  "nl",
  "sv",
  "cs",
  "no",
  "vi",
] as const;

export type AccountLocale = (typeof SUPPORTED_ACCOUNT_LOCALE_CODES)[number];

export const DEFAULT_ACCOUNT_LOCALE: AccountLocale = "en";
export const PLATFORM_LOCALE_COOKIE_NAME = "babel.platform.locale";
export const PLATFORM_LOCALE_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;

const ACCOUNT_LOCALE_SET = new Set<string>(SUPPORTED_ACCOUNT_LOCALE_CODES);

export function isAccountLocale(value: unknown): value is AccountLocale {
  return typeof value === "string" && ACCOUNT_LOCALE_SET.has(value);
}

/** Match a browser language against a surface's canonical locale registry. */
export function matchSupportedLocale<T extends string>(
  languageTag: string,
  supported: readonly T[],
): T | null {
  if (
    typeof languageTag !== "string" ||
    !languageTag.trim() ||
    languageTag === "*"
  )
    return null;
  let locale: Intl.Locale;
  try {
    locale = new Intl.Locale(languageTag.trim());
  } catch {
    return null;
  }
  const find = (code: string) =>
    supported.find((entry) => entry.toLowerCase() === code.toLowerCase()) ??
    null;
  const exact = find(locale.toString()) ?? find(locale.baseName);
  if (exact) return exact;
  const language = locale.language.toLowerCase();
  const region = locale.region?.toUpperCase();
  switch (language) {
    case "es":
      return (
        find(!region || region === "ES" ? "es-ES" : "es-419") ?? find("es")
      );
    case "fr":
      return find(region === "CA" ? "fr-CA" : "fr-FR") ?? find("fr");
    case "zh": {
      const script = locale.script?.toLowerCase();
      const traditional =
        script === "hant" ||
        (!script && ["TW", "HK", "MO"].includes(region ?? ""));
      return find(traditional ? "zh-Hant" : "zh-Hans");
    }
    case "pt":
      return find(!region || region === "BR" ? "pt-BR" : "pt-PT") ?? find("pt");
    case "nb":
    case "nn":
      return find("no");
    default:
      return find(language);
  }
}

export function matchAccountLocale(languageTag: string): AccountLocale | null {
  return matchSupportedLocale(languageTag, SUPPORTED_ACCOUNT_LOCALE_CODES);
}

export function matchSupportedLocaleFromAcceptLanguage<T extends string>(
  header: string | null | undefined,
  supported: readonly T[],
): T | null {
  if (!header) return null;
  const preferences = header
    .split(",")
    .map((entry, index) => parseLanguagePreference(entry, index))
    .filter((entry): entry is LanguagePreference => entry !== null)
    .sort(
      (left, right) => right.quality - left.quality || left.index - right.index,
    );
  for (const preference of preferences) {
    const locale = matchSupportedLocale(preference.languageTag, supported);
    if (locale) return locale;
  }
  return null;
}

export function matchAccountLocaleFromAcceptLanguage(
  header: string | null | undefined,
): AccountLocale | null {
  return matchSupportedLocaleFromAcceptLanguage(
    header,
    SUPPORTED_ACCOUNT_LOCALE_CODES,
  );
}

export function resolveAccountLocale(values: {
  storedLocale?: unknown;
  cookieLocale?: unknown;
  acceptLanguage?: string | null;
}): AccountLocale {
  if (values.storedLocale !== undefined && values.storedLocale !== null) {
    return isAccountLocale(values.storedLocale)
      ? values.storedLocale
      : DEFAULT_ACCOUNT_LOCALE;
  }

  if (values.cookieLocale !== undefined && values.cookieLocale !== null) {
    return isAccountLocale(values.cookieLocale)
      ? values.cookieLocale
      : DEFAULT_ACCOUNT_LOCALE;
  }

  return (
    matchAccountLocaleFromAcceptLanguage(values.acceptLanguage) ??
    DEFAULT_ACCOUNT_LOCALE
  );
}

type LanguagePreference = {
  index: number;
  languageTag: string;
  quality: number;
};

function parseLanguagePreference(
  value: string,
  index: number,
): LanguagePreference | null {
  const parts = value.split(";").map((part) => part.trim());
  const languageTag = parts[0];
  if (!languageTag || parts.length > 2) {
    return null;
  }

  let quality = 1;
  if (parts.length === 2) {
    const match = /^q=(0(?:\.\d{0,3})?|1(?:\.0{0,3})?)$/i.exec(parts[1]);
    if (!match) {
      return null;
    }
    quality = Number(match[1]);
  }

  if (quality <= 0) {
    return null;
  }

  return { index, languageTag, quality };
}
